import { useCallback, useEffect, useState } from "react";
import { getPrefs, savePrefs } from "../../../services/mfa/mfaSettingsApi";
import { describeMfaError } from "../../../services/mfa/mfaErrorMessages";
import { move as moveIn, orderFrom, rowsFrom } from "./methodOrder";

const LOAD_FAILED = "We couldn't load your sign-in methods.";

// docs/specs/login-setup.md §4: load the saved order, edit a draft, save it. The rows always come from the
// last prefs node sent plus the draft order, so a saved answer re-renders from the response.
const useMethodOrder = () => {
  const [prefs, setPrefs] = useState(null);
  const [draft, setDraft] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signInRequired, setSignInRequired] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const take = (body) => {
    setPrefs(body);
    setDraft(orderFrom(body?.priority));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSignInRequired(false);
    try {
      take(await getPrefs());
    } catch (e) {
      const detail = describeMfaError(e);
      setSignInRequired(detail.signIn);
      setError(detail.signIn ? detail.message : LOAD_FAILED);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const move = useCallback((index, delta) => {
    setDraft((list) => moveIn(list, index, delta));
    setSaved(false);
    setSaveError(null);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      take(await savePrefs({ priority: draft }));
      setSaved(true);
    } catch (e) {
      const detail = describeMfaError(e);
      setSignInRequired(detail.signIn);
      setSaveError(detail.message);
    } finally {
      setSaving(false);
    }
  }, [draft]);

  const changed = draft.join() !== orderFrom(prefs?.priority).join();
  return {
    rows: rowsFrom(prefs, draft),
    chosen: Boolean(prefs?.chosen),
    loading, error, signInRequired, changed, saving, saved, saveError,
    move, save, retry: load,
  };
};

export default useMethodOrder;
